import Vue from 'vue'
import config from '@/config/config.app.json'
import { CACHE_DB_NAME } from '@/plugins/caching/plugin'
import { OIDC } from './oidc-config'
import {
  applySessionResult,
  clearInvitationCode,
  clearLocalAuthState,
  errorBootTarget,
  fetchSession,
  readInvitationCode,
  saveOidcError,
  saveOidcReason,
} from './oidc-session'

/**
 * Page-load entry point. Must run before the Vue app is constructed. Inspects the URL and dispatches to one
 * of four mutually exclusive paths:
 *  1. Portal redirected back with ?error=...        -> save the error and show /login
 *  2. Portal redirected back with ?code=...         -> exchange OTC for token, then boot
 *  3. OIDC tenant, no callback params on the URL    -> probe SSO, maybe auto-redirect to /login
 *  4. Password tenant                               -> boot Vue normally
 *
 * @returns {Promise<void>|void} A promise that resolves once Vue has been lazy-loaded, or void on the
 *   SSO-redirect path (the browser navigates away).
 */
export function runOidcBootstrap() {
  const callbackParams = readCallbackParams()
  // Post-logout return: portal forwards ?reason=expired in the URL search. Move it into sessionStorage so
  // LoginPage can read it after Vue Router (hash mode) takes over, which doesn't see the search params.
  if (callbackParams.reason) saveOidcReason(callbackParams.reason)
  if (callbackParams.error) return handleCallbackError(callbackParams)
  if (callbackParams.isCallback) return handleCallback(callbackParams)
  if (config.authMethod === 'oidc') return probeSsoSessionThenBoot()
  return bootVueApp()
}

/**
 * Handles the Android return from the system browser. The portal sends mobile login and logout to its
 * app-callback route on the portal host; Android gives that URL to the app because the app claims the host as an
 * App Link, or the user taps the link on the portal fallback page. Login returns (code or error) go to the token
 * exchange, logout returns (a reason, no code) go to the login page.
 *
 * @param {string} rawUrl Full URL as delivered by ionic-plugin-deeplinks (its `url` field).
 * @returns {boolean} True when this was an app-callback URL and it was handled here.
 */
export function handleOidcAppLink(rawUrl) {
  if (typeof rawUrl !== 'string') return false
  // A relative string parses against the base, but a string that looks absolute is parsed on its own and throws
  // when it is not a valid URL ("http://[::1", "http://host:99999/"). The activity that receives App Links has to
  // be exported for App Links to work at all, so any app on the device can hand us one of those. An exception here
  // would escape into the App.vue deep-link callback, and because the plugin allows only one callback, the
  // device-link deep link handled after this call would stop working too. Anything unparseable is not our callback.
  let appCallbackUrl
  try {
    appCallbackUrl = new URL(rawUrl, window.location.href)
  } catch (error) {
    return false
  }
  if (!appCallbackUrl.pathname.endsWith(OIDC.appCallbackPath)) return false

  // The Custom Tab needs no close call here: AndroidLaunchMode is singleTask (cordova/config.xml), so Android
  // brings the running app to the front and finishes the tab above it when it delivers this URL.
  const code = appCallbackUrl.searchParams.get('code')
  const error = appCallbackUrl.searchParams.get('error')
  if (code || error) {
    processOidcCallback({
      code,
      error,
      errorDescription: appCallbackUrl.searchParams.get('error_description'),
    })

    return true
  }
  const reason = appCallbackUrl.searchParams.get('reason')
  if (reason) saveOidcReason(reason)
  window.location.hash = '/login'

  return true
}

/**
 * Registers the cordova-plugin-oauth callback listener on iOS OIDC tenants. The plugin dispatches a
 * `message` event with an `oauth::`-prefixed JSON payload when the system browser returns to the app's custom
 * scheme. Login callbacks (code/error) go to the token exchange; logout returns (reason, no code) go to the
 * login page. Cordova-only (checked through window.cordova); on web the URL-parsing path in runOidcBootstrap
 * handles the callback instead, and password tenants never reach here. Android uses the same plugin to open the
 * browser, but its return arrives as an App Link, not on the custom scheme, so this listener never fires there,
 * see handleOidcAppLink.
 *
 * @returns {void}
 */
export function attachOauthCallbackListener() {
  if (config.authMethod !== 'oidc' || !window.cordova) return
  window.addEventListener('message', (event) => {
    if (!event) return
    // Reject cross-origin injection: cordova-plugin-oauth dispatches the callback from native code as
    // window.dispatchEvent(new MessageEvent('message', { data: 'oauth::...' })), whose origin defaults to ''.
    // A postMessage from an embedded frame carries that frame's origin instead, so drop any non-empty origin
    // that is not our own document before trusting an oauth:: payload (it could otherwise inject an OTC).
    if (event.origin && event.origin !== window.location.origin) return
    if (typeof event.data !== 'string' || !event.data.startsWith('oauth::')) return
    let params
    try {
      params = JSON.parse(event.data.slice('oauth::'.length))
    } catch (e) {
      return
    }
    if (params.code || params.error) {
      processOidcCallback({
        code: params.code,
        error: params.error,
        errorDescription: params.error_description,
      })
      return
    }
    if (params.reason) saveOidcReason(params.reason)
    window.location.hash = '/login'
  })
}

// Reads OIDC params from both the hash query and the regular search. The portal redirects to the configured app
// callback with `?error=...&error_description=...`, but the params land in window.location.search (not the hash
// query) when the configured callback URL doesn't include /oidc-callback (e.g. just the SPA root). Checking both
// makes the error show up regardless of where they end up. The hash takes precedence when both are populated.
function readCallbackParams() {
  const hash = window.location.hash
  const isHashCallback = hash.startsWith('#' + OIDC.callbackPath)
  const isPathCallback = window.location.pathname === OIDC.callbackPath

  const hashQueryStart = hash.indexOf('?')
  const hashQueryString = hashQueryStart >= 0 ? hash.slice(hashQueryStart + 1) : ''
  const hashParams = new URLSearchParams(hashQueryString)
  const searchParams = new URLSearchParams(window.location.search.slice(1))

  const primaryParams = isHashCallback ? hashParams : searchParams
  return {
    isCallback: isHashCallback || isPathCallback,
    code: primaryParams.get('code'),
    error: hashParams.get('error') || searchParams.get('error'),
    errorDescription: hashParams.get('error_description') || searchParams.get('error_description') || '',
    reason: hashParams.get('reason') || searchParams.get('reason'),
  }
}

// Portal redirected here on OIDC failure (RFC 6749 section 4.1.2.1: error + error_description). While an
// invitation is being redeemed the error shows on the invitation page, otherwise on the login page.
function handleCallbackError({ error, errorDescription }) {
  console.error('OIDC error from portal callback:', error, errorDescription)
  saveOidcError(error, errorDescription)
  return bootVueApp(errorBootTarget())
}

function handleCallback({ code }) {
  // Invariant: a callback URL with no `code` and no `error` is now exclusively a post-logout return. The portal
  // appends `?reason=expired` to the app return URL on the post-logout chain; runOidcBootstrap moves that into
  // sessionStorage above, then dispatches here with `isCallback` true but `code` empty. Since the SPA-side
  // silent SSO probe was removed, no other path lands here without a code, so booting to /login is the right default.
  if (!code) return bootVueApp('/login')
  return exchangeOtcForToken(code)
}

/**
 * Run the OIDC callback exchange given a code (success) or error (IdP-side failure). On Cordova this is
 * driven by attachOauthCallbackListener, which parses the cordova-plugin-oauth `oauth::` message the system
 * browser delivers on the custom-scheme redirect.
 *
 * @param {{ code?: string, error?: string, errorDescription?: string }} params
 * @returns {void}
 */
export function processOidcCallback({ code, error, errorDescription } = {}) {
  if (code) {
    exchangeOtcForToken(code)
    return
  }
  if (error) {
    console.error('OIDC error from portal callback:', error, errorDescription || '')
    saveOidcError(error, errorDescription)
    bootVueApp(errorBootTarget())
  }
}

// State validation happens server-side at the IdP -> portal callback step (cookie-bound, see STATE_COOKIE_NAME
// in AuthOidcController), BEFORE the OTC is issued. The OTC itself is the bearer of the validated identity, so
// this SPA -> portal /exchange call is cookie-independent: it never needs the state cookie. This matters on
// Cordova, where two isolated cookie jars are in play:
//   - The system browser (Custom Tabs / ASWebAuthenticationSession) holds the state cookie during the IdP
//     roundtrip. It is separate from the native HTTP plugin's jar.
//   - The native HTTP plugin has its own jar and is used here for the /exchange POST. It never sees the state
//     cookie, and doesn't need to - the OTC stands on its own.
//
// On Cordova we route the POST through cordova-plugin-advanced-http (the same native HTTP path the rest of the
// app uses via rhRequest). The plugin issues the request from native code, bypassing the WKWebView CORS check
// that otherwise rejects the response: iOS WKWebView reports `Origin: null` for the bisaknosp document, and
// the portal's CORS handler (AbstractApiController, sources Allow-Origin from Referer) emits an Allow-Origin
// that doesn't include `null`. On web we use fetch as before.
// Remembers the most recent OTC sent to the portal so the single-use code is never exchanged twice. Defensive:
// if cordova-plugin-oauth ever delivers the callback message more than once for a single login, the second
// exchange would 401 ("Invalid or expired code") and show an error toast after an otherwise-successful login.
// Keyed on the code rather than a boolean so a later login (new code) still works.
let lastExchangedOtc = null

function exchangeOtcForToken(code) {
  if (code === lastExchangedOtc) return
  lastExchangedOtc = code
  if (window.cordova && window.cordova.plugin && window.cordova.plugin.http) {
    return exchangeOtcViaCordovaHttp(code)
  }
  return fetch(OIDC.exchangeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, app: OIDC.appId }),
  })
    .then(parseExchangeResponse)
    .then(handleExchangeResult)
    .catch(handleExchangeError)
}

function exchangeOtcViaCordovaHttp(code) {
  return new Promise((resolve) => {
    const http = window.cordova.plugin.http
    http.setDataSerializer('json')
    http.sendRequest(
      OIDC.exchangeUrl,
      {
        method: 'post',
        data: { code, app: OIDC.appId },
        headers: { 'Content-Type': 'application/json' },
      },
      (response) => {
        const body = parseCordovaHttpJson(response && response.data)
        if (!body) {
          handleExchangeError(new Error('Exchange response was not valid JSON'))
        } else {
          handleExchangeResult(body)
        }
        resolve()
      },
      (response) => {
        const body = parseCordovaHttpJson(response && response.error)
        const apiMessage = body && body.data && body.data.message
        const status = (response && response.status) || 'unknown'
        handleExchangeError(new Error(apiMessage || ('Exchange failed: ' + status)))
        resolve()
      }
    )
  })
}

function parseCordovaHttpJson(raw) {
  if (raw && typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

// Use the portal's error body ("Invalid or expired code", "App identifier mismatch", etc.) instead of just
// the HTTP status, so the .catch handler logs the real reason for debugging. The user-facing toast still uses
// the generic translation key.
function parseExchangeResponse(response) {
  if (response.ok) return response.json()
  return response.json()
    .catch(() => null)
    .then((body) => {
      const apiMessage = body && body.data && body.data.message
      throw new Error(apiMessage || ('Exchange failed: ' + response.status))
    })
}

function handleExchangeResult(result) {
  const hasToken = !!(result && result.data && result.data.token)
  if (hasToken && readInvitationCode()) {
    // The new session belongs to the invited account. Drop the previous session and its cache first, so neither
    // a stale site nor the old token survives. A cancelled invitation login never gets here.
    clearLocalAuthState()
    deleteCacheDatabase()
  }
  if (applySessionResult(result)) {
    // The invitation, if any, is redeemed by now
    clearInvitationCode()
    // Land on the first configured nav entry: some clients have no home page, so homeTopNavOrder defines the
    // landing route (same pattern as LoginPage). Fall back to the default route if the key is absent or empty,
    // rather than throwing inside the success path.
    const firstNav = (config.homeTopNavOrder || '').split(',')[0]
    return bootVueApp(firstNav ? '/' + firstNav : undefined).then(populateActiveSite)
  }
  // Portal returned 200 but no token in the body: shape mismatch, expired OTC, or app-id rejection.
  // Save an error so the next page shows a toast instead of the user landing silently on /login.
  console.error('OIDC exchange failed:', result)
  saveOidcError('exchange_failed', 'Token exchange returned no token')
  return bootVueApp(errorBootTarget())
}

// The cache plugin is not installed yet at this point, so delete by name. Other tabs close their connection on
// versionchange (caching.js); the cache plugin's own open later queues behind this delete.
function deleteCacheDatabase() {
  if (!window.indexedDB) return
  const request = window.indexedDB.deleteDatabase(CACHE_DB_NAME)
  request.onblocked = () => console.warn('Cache delete is waiting for another tab to close the database')
}

// Password login calls $rhAuth.storeActiveSite() inside auth.js to populate localStorage.activeSite. The
// rhRequest plugin reads that key and emits the Selected-Site-Id header on every API call; without it the
// portal eventually 401s a site-scoped endpoint, which trips unauthorizedCallback -> $rhAuth.logout() and
// (on Cordova) opens the OIDC logout in the system browser via cordova-plugin-oauth. The OIDC exchange path needs to
// reproduce that storeActiveSite() call so subsequent home-page polling has the header.
function populateActiveSite() {
  const rhAuth = Vue.prototype.$rhAuth
  if (rhAuth && typeof rhAuth.storeActiveSite === 'function') {
    rhAuth.storeActiveSite()
  }
}

function handleExchangeError(error) {
  // Network failure or non-2xx HTTP from the portal /exchange endpoint.
  console.error('OIDC exchange error:', error)
  saveOidcError('exchange_failed', error && error.message ? error.message : 'Token exchange request failed')
  return bootVueApp(errorBootTarget())
}

// Portal SSO probe on every OIDC bootstrap. Two purposes:
//  1. Zero-click SSO when not logged in here but already authenticated in another portal app: the rh_oidc_session
//     cookie is shared on the portal origin, so the portal's /session endpoint returns a fresh token.
//  2. Refresh role/permissionsId for already-logged-in users - the portal-side SSO cache is the source of truth
//     and reflects DB role changes on the next callback. Without this, localStorage retains the role from the
//     initial login until full logout.
// For already-logged-in users, an expired/absent SSO cookie must NOT redirect to the IdP - let the normal 401
// handling log them out via request.js when the API rejects their stale token. For new users with no portal
// session, just boot to LoginPage; the user clicks "Login with NEW" to start the real OIDC flow. We deliberately
// do NOT run an IdP-side silent probe (prompt=none) here: NEW IdP doesn't honour prompt=none on test, so the
// probe traps the user on its hosted login UI instead of returning login_required to the portal redirect_uri.
function probeSsoSessionThenBoot() {
  // On Cordova the SSO probe can't succeed, so skip it. WKWebView sends `Origin: null`, so the portal's CORS check
  // rejects a plain fetch to /auth-oidc/session (the same reason the exchange uses the native HTTP plugin), and
  // the WebView doesn't share the IdP cookie jar with the system browser anyway. fetchSession() has no timeout, so
  // waiting on it would stall the splash on a slow or unreachable network before first paint. Boot directly and let
  // the normal 401 path in request.js log out a stale token. Detect Cordova via window.cordova (set synchronously
  // while cordova.js runs), not window.usingCordova (set on its onload), which may not be set yet this early.
  if (window.cordova) return bootVueApp()
  return fetchSession()
    .then((response) => {
      if (!response.ok) throw new Error('No session')
      return response.json()
    })
    .then((result) => {
      // applySessionResult writes to localStorage when the body carries a token; either way we boot from here
      // (no IdP fallback redirect).
      applySessionResult(result)
      return bootVueApp()
    })
    .catch(() => bootVueApp())
}

// Sets the URL hash and lazy-loads the Vue app. Vue Router (hash mode) reads window.location synchronously on
// init, so the hash must be set before the dynamic import. When called after Vue is already running (e.g. from
// the Cordova oauth callback listener), the import is a no-op and the hash change triggers Vue Router navigation.
//
// Also scrubs OIDC callback params from window.location.search so a manual reload doesn't re-exchange the
// single-use OTC ("Invalid or expired code") or replay the post-logout toast. We delete only the OIDC keys and
// preserve everything else - bootVueApp is also called from the password-tenant boot path, so a blanket scrub
// would silently strip any landed-page params (UTM, etc.) on every tenant. Vue Router is hash-based here, so
// remaining search params are only meaningful to whatever landed-page code chooses to read them.
function bootVueApp(hash) {
  const params = new URLSearchParams(window.location.search.slice(1))
  const oidcKeys = ['code', 'error', 'error_description', 'reason', 'state']
  let stripped = false
  for (const key of oidcKeys) {
    if (params.has(key)) {
      params.delete(key)
      stripped = true
    }
  }
  if (stripped) {
    const newSearch = params.toString()
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash
    window.history.replaceState({}, document.title, newUrl)
  }
  if (hash) window.location.hash = hash
  return import('@/app-init.js')
}
